import { ReactElement } from 'react';

import { FileWithPath } from '@mantine/dropzone';
import { ChatAttachmentType } from 'shared/interfaces/ChatMessage';
import { Conversation } from 'shared/interfaces/Conversation';
import { FacebookAccount } from 'shared/interfaces/FacebookAccount';
import { InstagramAccount } from 'shared/interfaces/InstagramAccount';
import { Memory } from 'shared/interfaces/Memory';
import { Resource, ResourceWithShopifyStore, UnUploadedResource } from 'shared/interfaces/Resource';
import { TelephonyConfiguration, TelephonyConfigurationWithIntegrations } from 'shared/interfaces/Telephony';
import { ToolConfig } from 'shared/interfaces/tools/Tool';
import { TranscriptionConfiguration } from 'shared/interfaces/Transcription';
import { DeployConfiguration, VoiceConfiguration } from 'shared/interfaces/Voice';
import { WhatsAppAccount } from 'shared/interfaces/WhatsAppAccount';

export interface SmartAssistant {
  _id: string;
  name: string;
  welcomeMessage: string;
  createdAt: number;
  organisation: string;
  creator: string;
  teams: string[];
  shopifyStoreResource: string;
  resources: string[];
  nMessages: number;
  nSessions: number;
  assistantMessageColor: string;
  userMessageColor: string;
  conversationBackgroundColor: string;
  secondaryBackgroundColor: string;
  gradientColors: string[];
  showBadge: boolean;
  enabledEmoji: boolean;
  avatarKey: string;
  // This is a virtual field that isn't stored in the database
  avatarUrl: string;
  customSystemPrompt: string;
  model: SmartAssistantModel;
  restrictAnswersToResourcesContentResponse: string;
  accessLevel: AccessLevel;
  type: SmartAssistantType;
  pausedIntegration?: boolean;
  tags: string[];
  memories?: Memory[];
  dashboardSharingConfiguration?: DashboardSharingConfiguration;
  discordIntegration?: DiscordIntegration;
  websiteIntegration?: WebsiteIntegration;
  whatsAppIntegration?: WhatsAppIntegration;
  facebookIntegration?: FacebookIntegration;
  instagramIntegration?: InstagramIntegration;
  chatAttachmentConfiguration?: ChatAttachmentConfiguration;
  toolConfigs?: ToolConfig[];
  whiteLabelIntegration?: WhiteLabelIntegration;
  voiceIntegration?: VoiceIntegration;
}

export interface SmartAssistantWithIntegrations
  extends Omit<
    SmartAssistant,
    'shopifyStoreResource' | 'whatsAppIntegration' | 'facebookIntegration' | 'instagramIntegration' | 'voiceIntegration'
  > {
  shopifyStoreResource?: ResourceWithShopifyStore;
  whatsAppIntegration?: WhatsAppIntegrationWithIntegrations;
  facebookIntegration?: FacebookIntegrationWithIntegrations;
  instagramIntegration?: InstagramIntegrationWithIntegrations;
  voiceIntegration?: VoiceIntegrationWithIntegrations;
}

export interface GuestSmartAssistantData {
  smartAssistant: SmartAssistant;
  conversation?: Conversation;
  removePoweredBy: boolean;
}

export interface GuestSmartAssistantDataWithIntegrations extends Omit<GuestSmartAssistantData, 'smartAssistant'> {
  smartAssistant: SmartAssistantWithIntegrations;
}

export interface SmartAssistantStats {
  nMessages: number;
  nSessions: number;
  nMessagesPerSession: number;
}

export interface DashboardSharingConfiguration {
  isEnabled: boolean;
}

export interface ChatAttachmentConfiguration {
  isImageEnabled: boolean;
  isAudioEnabled: boolean;
}

export interface DiscordIntegration {
  serverId: string;
  inviteLink: string;
  invocationCommand: string;
}

export interface DiscordInviteLinkValidationDto {
  isValid: boolean;
  serverId: string;
}

export interface WebsiteIntegration {
  allowedDomains: string[];
  bubbleAlignment: SmartAssistantBubbleAlignment;
  bubblePopupMessages: string[];
  welcomeHeaderTitle?: string;
  welcomeHeaderSubtitle?: string;
}

export interface WhiteLabelIntegration {
  whiteLabelDomain: string;
  domain: string;
  subDomain: string;
}

export interface WhatsAppIntegration {
  whatsAppAccount: string;
  whatsAppPhoneNumberId: string;
}

export interface WhatsAppIntegrationWithIntegrations extends Omit<WhatsAppIntegration, 'whatsAppAccount'> {
  whatsAppAccount: WhatsAppAccount;
}

export interface VoiceIntegration {
  voiceConfiguration: VoiceConfiguration;
  transcriptionConfiguration: TranscriptionConfiguration;
  telephonyConfiguration?: TelephonyConfiguration;
  deployConfiguration?: DeployConfiguration;
}

export interface VoiceIntegrationWithIntegrations extends Omit<VoiceIntegration, 'telephonyConfiguration'> {
  telephonyConfiguration?: TelephonyConfigurationWithIntegrations;
}

export interface FacebookIntegration {
  facebookAccount: string;
}

export interface FacebookIntegrationWithIntegrations extends Omit<FacebookIntegration, 'facebookAccount'> {
  facebookAccount: FacebookAccount;
}

export interface InstagramIntegration {
  instagramAccount: string;
}

export interface InstagramIntegrationWithIntegrations extends Omit<InstagramIntegration, 'instagramAccount'> {
  instagramAccount: InstagramAccount;
}

export enum SmartAssistantBubbleAlignment {
  LEFT = 'left',
  RIGHT = 'right'
}

export enum AccessLevel {
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
  PROTECTED = 'PROTECTED',
  NULL = ''
}

export enum SmartAssistantType {
  BASIC = 'BASIC',
  SHOPIFY = 'SHOPIFY',
  BIGCOMMERCE = 'BIGCOMMERCE',
  WIX = 'WIX',
  WOOCOMMERCE = 'WOOCOMMERCE',
  SQUARESPACE = 'SQUARESPACE',
  DISCORD = 'DISCORD',
  INSTAGRAM = 'INSTAGRAM',
  FACEBOOK = 'FACEBOOK',
  SMS = 'SMS',
  WHATSAPP = 'WHATSAPP',
  WEBSITE = 'WEBSITE',
  PHONE = 'PHONE'
}

export type SmartAssistantVoiceModel = SmartAssistantModel.GPT_4_O_REALTIME | SmartAssistantModel.GPT_4_O_MINI_REALTIME;

export enum SmartAssistantModel {
  // OpenAI models
  GPT_4_O = 'gpt-4o',
  GPT_4_O_MINI = 'gpt-4o-mini',
  GPT_4_O_REALTIME = 'gpt-4o-realtime-preview-2024-12-17',
  GPT_4_O_MINI_REALTIME = 'gpt-4o-mini-realtime-preview-2024-12-17',

  // Anthropic models
  CLAUDE_3_7_SONNET = 'claude-3-7-sonnet',
  CLAUDE_3_5_SONNET = 'claude-3-5-sonnet',
  CLAUDE_3_5_HAIKU = 'claude-3-5-haiku',

  // Google models
  GOOGLE_GEMINI_PRO_1_5 = 'gemini-pro-1.5',
  GOOGLE_GEMINI_FLASH_1_5 = 'gemini-flash-1.5',
  GOOGLE_GEMINI_FLASH_2_0 = 'gemini-flash-2.0'
}

export interface CreateSmartAssistantDto {
  selectedResources: (Resource | UnUploadedResource)[];
  avatar?: FileWithPath;
  type: SmartAssistantType;
  shopifyStoreResource?: string;
  configuration: {
    customSystemPrompt: string;
    enabledEmoji?: boolean;
    model: SmartAssistantModel;
    restrictAnswersToResourcesContentResponse?: string;
    accessLevel: AccessLevel;
    password?: string;
    whiteLabelIntegration?: WhiteLabelIntegration;
  };
  customization: {
    name: string;
    welcomeMessage?: string;
    assistantMessageColor?: string;
    userMessageColor?: string;
    conversationBackgroundColor?: string;
    secondaryBackgroundColor?: string;
    gradientColors?: string[];
    showBadge?: boolean;
  };
  discordIntegration?: DiscordIntegration;
  websiteIntegration?: WebsiteIntegration;
  whatsAppIntegration?: WhatsAppIntegration;
  voiceIntegration?: VoiceIntegration;
  facebookIntegration?: FacebookIntegration;
  instagramIntegration?: InstagramIntegration;
  chatAttachmentConfiguration?: ChatAttachmentConfiguration;
  toolConfigs?: ToolConfig[];
  tags?: string[];
}

export interface UpdateSmartAssistantDto {
  id: string;
  teams?: string[];
  name?: string;
  welcomeMessage?: string;
  resourceIds?: string[];
  shopifyStoreResource?: string;
  assistantMessageColor?: string;
  userMessageColor?: string;
  conversationBackgroundColor?: string;
  secondaryBackgroundColor?: string;
  gradientColors?: string[];
  showBadge?: boolean;
  enabledEmoji?: boolean;
  customSystemPrompt?: string;
  model?: SmartAssistantModel;
  accessLevel?: AccessLevel;
  tags?: string[];
  memories?: Memory[];
  password?: string;
  whiteLabelIntegration?: WhiteLabelIntegration;
  dashboardSharingConfiguration?: DashboardSharingConfiguration;
  chatAttachmentConfiguration?: ChatAttachmentConfiguration;
}

export interface UpdateSmartAssistantIntegrationsDto {
  smartAssistantId: string;
  discordIntegration?: DiscordIntegration;
  websiteIntegration?: WebsiteIntegration;
  whatsAppIntegration?: WhatsAppIntegration;
  facebookIntegration?: FacebookIntegration;
  instagramIntegration?: InstagramIntegration;
  voiceIntegration?: VoiceIntegration;
}

export interface UpdateSmartAssistantAvatarDto {
  id: string;
  avatarFile: FileWithPath;
}

export interface UploadAttachmentDto {
  smartAssistantId: string;
  conversationId: string;
  type: ChatAttachmentType;
  file: FileWithPath;
}

export enum SmartAssistantCreationProgress {
  UPLOADING_RESOURCES,
  CREATING_SMART_ASSISTANT,
  UPLOADING_AVATAR,
  CONFIGURING_TOOLS,
  DONE
}

export interface ValidateGuestPasswordDto {
  smartAssistantId: string;
  password: string;
}

export interface SmartAssistantVisualConfig {
  assistantName: string;
  assistantWelcomeMessage: string;
  assistantAvatar: ReactElement;
  assistantMessageColor: string;
  userMessageColor: string;
  conversationBackgroundColor: string;
  secondaryBackgroundColor: string;
  gradientColors: string[];
  showBadge: boolean;
  assistantBadgeTitle?: string;
}

export type SmartAssistantColorTheme = Omit<
  SmartAssistantVisualConfig,
  'assistantName' | 'assistantAvatar' | 'showBadge' | 'assistantWelcomeMessage' | 'assistantBadgeTitle'
>;

// Form interfaces
export interface ConfigurationFormValues {
  customSystemPrompt: string;
  restrictAnswersToResourcesContent: boolean;
  restrictAnswersToResourcesContentResponse: string;
  enabledEmoji?: boolean;
  model: SmartAssistantModel;
}

export interface CustomizationFormValues {
  name: string;
  welcomeMessage?: string;
  assistantMessageColor?: string;
  userMessageColor?: string;
  conversationBackgroundColor?: string;
  secondaryBackgroundColor?: string;
  gradientColors?: string[];
  showBadge?: boolean;
}

export interface DiscordIntegartionFormValues {
  discordInviteLink: string;
  discordInvocationCommand: string;
}

export interface WhatsAppIntegrationFormValues {
  whatsAppAccount: string;
  whatsAppPhoneNumberId: string;
}

export interface FacebookIntegrationFormValues {
  facebookAccount: string;
}

export interface InstagramIntegrationFormValues {
  instagramAccount: string;
}

export interface WebsiteConfigurationFormValues {
  allowedDomains: string[];
  bubbleAlignment: SmartAssistantBubbleAlignment;
  bubblePopupMessages: string[];
  welcomeHeaderTitle?: string;
  welcomeHeaderSubtitle?: string;
}

export interface PhoneAgentBasicsFormValues {
  name: string;
  welcomeMessage: string;
  voiceConfiguration: VoiceConfiguration;
  transcriptionConfiguration: TranscriptionConfiguration;
}

export interface MessagingSmartAssistantBasicsFormValues {
  name: string;
}

export interface TelephonyFormValues {
  telephonyConfiguration?: TelephonyConfiguration;
}

export interface ChatAttachmentConfigurationFormValues {
  isImageEnabled: boolean;
  isAudioEnabled: boolean;
}
